import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import TextInput from '~components/FormFields/v2/TextInput';
import { M2MClient } from '~hooks/useApiClients/models';
import { RoleItem } from '~hooks/useUsers/models';

import {
  ApiClientDto,
  apiClientSchema,
  setApiClientSchemaDefaultValues,
} from './apiClientSchema';

export type ApiClientFormHandler = {
  onSubmit: () => Promise<ApiClientDto | undefined>;
};

interface ApiClientFormProps {
  apiClient?: M2MClient;
  availableRoles: RoleItem[];
}

const ApiClientForm = forwardRef<ApiClientFormHandler, ApiClientFormProps>(
  function ApiClientForm({ apiClient, availableRoles }, ref) {
    const isEditing = Boolean(apiClient);

    const {
      control,
      formState: { errors },
      handleSubmit,
    } = useForm<ApiClientDto>({
      resolver: yupResolver(apiClientSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: setApiClientSchemaDefaultValues(apiClient, availableRoles),
    });

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve) => {
            handleSubmit(
              (data) => resolve(data),
              () => resolve(undefined),
            )();
          });
        },
      }),
      [handleSubmit],
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}>
        <Box>
          <TextInput
            control={control}
            slotProps={{
              htmlInput: { 'data-ui': 'page-settings-api-clients__form__name-input' },
            }}
            isRequired
            label={t('form_fields.name')}
            name="name"
          />
        </Box>

        <Box>
          <TextInput
            control={control}
            slotProps={{
              htmlInput: { 'data-ui': 'page-settings-api-clients__form__slug-input' },
            }}
            label={t('administration.api_clients.slug_label')}
            name="slug"
            placeholder={t('administration.api_clients.slug_placeholder')}
          />
        </Box>

        <Box>
          <AutocompleteFormField
            control={control}
            dataUi="page-settings-api-clients__form__roles-input"
            errors={errors}
            disabled={isEditing}
            getLabel={(role: RoleItem) => role.name}
            getValue={(role: RoleItem) => role.id}
            isRequired
            label={t('administration.api_clients.roles_label')}
            list={availableRoles}
            multiple
            name="roles"
          />
        </Box>
      </Box>
    );
  },
);

export default ApiClientForm;
